import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { researchNavigate, useResearchLocation } from "./researchNavigation";
import "./researchDesk.css";
import "./researchCoverage.css";
import "./phase0.css";
import { price, cleanNumbers, observation, normalizeLevels, editorialStories } from "./researchData";
import { prefetchResearch } from "../../../researchBoot";
import { useResearchTheme } from '../ui/useResearchTheme';
import { useLiveQuotes, formatChicagoTime } from "./useLiveQuotes";
import { useRubberBandScroll } from "../../../lib/motion";
import { ResearchNav, PriceTag, ToneBadge, MissingBadge, StaleBadge, SectionHeader, HeroBrief, StoryRow, LevelBand, ZoneBand, CalendarItem } from '../ui/ResearchUI';
import RevisionNotice from '../newsletter/RevisionNotice';
import {editionRoute, editionLink, revisionInfo} from '../newsletter/editionLinks';
import ResearchArchive from '../newsletter/ResearchArchive';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/research/editions`;
const NewsletterEdition = lazy(()=>import('../newsletter/NewsletterEdition'));
const DISCLAIMER = "AI-assisted research compiled from the sources listed. Timestamps vary by source. Levels and liquidation zones are modeled estimates. Derivatives describe positioning, not a prediction. Not financial advice.";

function Link({ to, children, ...props }) {
  return <a href={to} {...props} onClick={event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); researchNavigate(to);
  }}>{children}</a>;
}

function Chip({ tone = "neutral", children }) {
  return <span className={`rd-chip rd-${tone}`}>{children}</span>;
}

function Archive({ editions, current }) {
  return <ul className="rd-archive">{editions.map((edition,index) => <li key={edition.editionId || `${edition.date}-${index}`}>
    <Link to={editionLink(edition)} aria-current={edition.date === current ? "page" : undefined}>
      <time dateTime={edition.date}>{edition.date}</time><span>{edition.headline}</span>
    </Link>
  </li>)}</ul>;
}

function SourceRef({ index }) {
  return Number.isInteger(index) && index > 0 ? <sup><a className="rd-source-ref" href={`#rd-source-${index}`} aria-label={`Source ${index}`} onClick={() => { const details = document.querySelector('.rd-source-details'); if (details) details.open = true; requestAnimationFrame(() => document.getElementById(`rd-source-${index}`)?.scrollIntoView({block: 'center'})); }}>{index}</a></sup> : null;
}

export function CitedText({text, sources = []}) {
  return cleanNumbers(text).split(/(\[\d+\])/g).map((part,index) => {
    const citation = part.match(/^\[(\d+)\]$/);
    return citation ? Number(citation[1]) <= sources.length ? <SourceRef key={index} index={Number(citation[1])} /> : <MissingBadge key={index}>missing source</MissingBadge> : part;
  });
}

function updatedTime(edition) {
  if (!edition?.publishedAt) return edition?.updated;
  const date = new Date(edition.publishedAt);
  if (Number.isNaN(date.getTime())) return edition.updated;
  const format = zone => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: zone, timeZoneName: "short" }).format(date);
  return `${format("America/Chicago")} / ${format("America/New_York")}`;
}

function tapeValue(tick) {
  if (!['BTC', 'ETH', 'SOL', 'XRP'].includes(tick.label) || !/^\$[\d,.]+$/.test(tick.value)) return tick.value;
  return price(Number(tick.value.replace(/[$,]/g, '')));
}

function tapeWindow(edition) {
  const asof = edition.tape.find(tick => tick.asof)?.asof;
  return edition.window && asof
    ? `${edition.window} on ${asof.slice(0, 10)} · Snapshot ${new Date(asof).toLocaleTimeString('en-US', {timeZone: 'UTC', timeZoneName: 'short'})}`
    : `${edition.window || 'Edition snapshot'} · ${edition.date} · ${edition.updated}`;
}

function Metrics({ rows, derivatives = false }) {
  const [expanded, setExpanded] = useState({});
  // Spring-feel: horizontal rubber-band overscroll on this real scroll
  // container (overflow-x: auto). Touch only; mouse/wheel left to browser.
  const rubber = useRubberBandScroll("x");
  return <div className="rd-tablewrap" ref={rubber.ref} style={rubber.style} tabIndex={0} role="region" aria-label={derivatives ? "Derivatives tone table" : "Flows and macro table"}>
    <table><thead><tr><th scope="col">Metric</th><th scope="col">Reading</th><th scope="col">Context</th>{derivatives && <th scope="col">Tone</th>}</tr></thead>
      <tbody>{rows.map((row, index) => <React.Fragment key={`${row.metric}-${index}`}><tr><th scope="row">{row.metric} <SourceRef index={row.sourceIndex} />
        {!!row.venues?.length && <button className="rd-venue-toggle" type="button" aria-expanded={!!expanded[index]} aria-controls={`rd-venues-${index}`} onClick={() => setExpanded(previous => ({...previous, [index]: !previous[index]}))}>{expanded[index] ? 'Hide venues' : 'Show venues'}</button>}</th>
        <td className="rd-number">{cleanNumbers(row.reading) || '—'} {!row.reading && <MissingBadge/>}{!derivatives && observation(row).date && <small className="rd-observation">{observation(row).date} {observation(row).stale && <StaleBadge/>}</small>}</td><td className="rd-context">{cleanNumbers(row.context)}</td>
        {derivatives && <td><ToneBadge tone={row.tone} label={row.toneLabel} context={row.context}/></td>}
      </tr>{!!row.venues?.length && expanded[index] && <tr id={`rd-venues-${index}`} className="rd-venue-detail"><td colSpan={derivatives ? 4 : 3}><ul>{row.venues.map(venue => <li key={venue.name}><strong>{venue.name}</strong> <span className="rd-number">{venue.reading}</span><span>{venue.context} <SourceRef index={venue.sourceIndex} /></span></li>)}</ul></td></tr>}</React.Fragment>)}</tbody>
    </table>
  </div>;
}

export default function ResearchDesk() {
  const location = useResearchLocation();
  const path = location === "/" ? "/research" : location;
  const [editions, setEditions] = useState([]);
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [activeTag, setActiveTag] = useState("All");
  const [copyMessage, setCopyMessage] = useState("");
  const [theme, changeTheme] = useResearchTheme();
  const summaryRef = useRef(null);
  const isArchive = path === "/research/archive";
  const selectedRoute = editionRoute(path);
  const known = path === "/research" || isArchive || !!selectedRoute;
  const liveQuotes = useLiveQuotes(known && !isArchive && !selectedRoute);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    let disposed = false;
    const request = async url => {
      const response = await fetch(url, { signal: controller.signal });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(response.status === 401 || response.status === 403
        ? "Your session cannot read the research store. Sign in again."
        : "The research store is unavailable. Please retry.");
      return response.json();
    };
    setLoading(true); setError(""); setEdition(null); setActiveTag("All"); setCopyMessage("");
    if (!known) { setLoading(false); return () => { disposed = true; clearTimeout(timeout); controller.abort(); }; }
    (async () => {
      try {
        const [archive, selected] = await Promise.all([
          isArchive ? Promise.resolve({editions:[],count:0}) : request(`${API}?limit=90`),
          (isArchive ? Promise.resolve(null) : selectedRoute ? request(`${API}${selectedRoute.endpoint}`) : prefetchResearch().then(value => value || request(`${API}/latest`))).then(value => { if (!disposed) setEdition(value); return value; }),
        ]);
        if (!disposed) { setEditions(archive?.editions || []); setEdition(selected); }
      } catch (failure) {
        if (!disposed) setError(failure.name === "AbortError" ? "The research request timed out. Please retry." : failure.message);
      } finally {
        clearTimeout(timeout);
        if (!disposed) setLoading(false);
      }
    })();
    return () => { disposed = true; clearTimeout(timeout); controller.abort(); };
    // The path is the request identity; dateMatch is intentionally derived above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, retry]);

  useEffect(() => {
    if (!known || isArchive) return undefined;
    let disposed = false;
    let inFlight = false;
    let controller;
    const refresh = async () => {
      if (disposed || document.hidden || inFlight) return;
      inFlight = true;
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      const read = async url => {
        const response = await fetch(url, { signal: controller.signal, cache: "no-store" });
        if (!response.ok) throw new Error("Background refresh unavailable");
        return response.json();
      };
      try {
        const [archive, selected] = await Promise.all([
          read(`${API}?limit=90`),
          isArchive ? Promise.resolve(null) : read(`${API}${selectedRoute?.endpoint || '/latest'}`),
        ]);
        if (!disposed) {
          setEditions(archive.editions || []);
          if (selected) setEdition(selected);
          setError("");
        }
      } catch {
        // Retain the last successful edition and its visible publication time.
      } finally {
        clearTimeout(timeout);
        inFlight = false;
      }
    };
    const interval = setInterval(refresh, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      disposed = true;
      clearInterval(interval);
      controller?.abort();
      document.removeEventListener("visibilitychange", refresh);
    };
    // The route identifies the selected edition; preserve reading/filter state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (!copyMessage) return undefined;
    const timeout = setTimeout(() => setCopyMessage(""), 4000);
    return () => clearTimeout(timeout);
  }, [copyMessage]);

  async function copySummary() {
    const text = [edition.headline, "", ...edition.summary.map(line => `• ${line}`), "", `Bottom line: ${edition.bottomLine}`, `AEON Research · ${edition.date}`].join("\n");
    try { await navigator.clipboard.writeText(text); setCopyMessage("Summary copied"); }
    catch {
      const selection = window.getSelection();
      if (summaryRef.current && selection) {
        const range = document.createRange(); range.selectNodeContents(summaryRef.current);
        selection.removeAllRanges(); selection.addRange(range);
      }
      setCopyMessage("Summary selected. Use your device’s Copy action.");
    }
  }

  const stories = editorialStories(edition);
  const tags = ["All", ...new Set(stories.map(story => story.tag))];
  const title = isArchive ? "Research Archive" : "Daily Market Summary";
  const newsletter = edition?.newsletter?.version === 2;
  const stateTitle = loading ? "Loading the edition" : error ? "Research is unavailable"
    : !known ? "Page not found" : selectedRoute ? "This edition has not been published" : "The first edition is on its way";

  return <div className="research-desk" data-theme={theme}>
    <ResearchNav theme={theme} onThemeChange={changeTheme}/>
    {!newsletter && !isArchive && <SectionHeader page title={title} timestamp={(edition || editions[0]) ? `Updated ${updatedTime(edition || editions[0])}` : loading ? 'Loading' : 'No edition loaded'} archive purpose="Crypto markets through a derivatives lens: what moved, how positioning changed, where the levels are, and what comes next."/>}
    {!newsletter && !isArchive && !selectedRoute && known && <section className="rd-coverage-entry" aria-label="Asset coverage"><div><strong>Derivatives asset coverage</strong><p>View the latest three-venue audit, 30-day history checks and missing observations.</p></div><Link to="/research/coverage">View coverage audit →</Link></section>}
    {edition && <section className="rd-tape" aria-label={liveQuotes?.quotes?.length ? "Live prices" : "Edition price snapshot"} aria-live="polite" aria-atomic="true"><div>{liveQuotes?.quotes?.length ? liveQuotes.quotes.map(quote => { const tick = edition.tape.find(t => t.label === quote.symbol); return <PriceTag key={quote.symbol} sym={quote.symbol} value={quote.price} formatted={price(quote.price)} change={tick?.changePercent} changeText={tick?.change} dir={tick?.dir} asof={quote.asof} label={liveQuotes.mode === "live" ? "Live" : "Snapshot"} />; }) : edition.tape.map((tick, index) => <PriceTag key={`${tick.label}-${index}`} sym={tick.label} formatted={tapeValue(tick)} change={tick.changePercent} changeText={tick.change} dir={tick.dir} asof={tick.asof}/>)}</div><p>{liveQuotes?.quotes?.length ? `${liveQuotes.mode === "live" ? "LIVE" : "Snapshot"} prices · Austin ${formatChicagoTime(liveQuotes.asof) || formatChicagoTime(liveQuotes.quotes[0].asof)} · change from edition snapshot` : tapeWindow(edition)}</p></section>}

    <main className={`rd-grid${newsletter ? ' an-layout':''}`} aria-busy={loading}>
      {isArchive && !loading && !error ? <ResearchArchive/>
        : !edition ? <section className="rd-state" role={error ? "alert" : "status"}><div className="rd-kicker">AEON Research</div><h1>{stateTitle}</h1>
          <p>{error || (selectedRoute ? `No edition is available for ${selectedRoute.date}.` : "Published editions will appear here with their market snapshot, sources, calendar, and key levels.")}</p>
          {error && <button className="rd-button" type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>}
          {!loading && <Link to="/research/archive">Browse the archive</Link>}</section>
          : newsletter ? <Suspense fallback={<section className="rd-state" role="status"><h1>{edition.headline}</h1><p>Loading the newsletter…</p></section>}><NewsletterEdition key={edition.editionId || edition.date} edition={edition} original={selectedRoute?.original}/></Suspense> : <>
            <article className="rd-article">
              <HeroBrief edition={{...edition,byline:revisionInfo(edition).byline}}>
              <RevisionNotice edition={edition} original={selectedRoute?.original}/>
              <section className="rd-summary" aria-label="AEON Summary"><div className="rd-summary-head"><h2>AEON Summary</h2><button type="button" className="rd-button" onClick={copySummary}>Copy summary</button></div>
                <ul ref={summaryRef}>{edition.summary.map((bullet, index) => <li key={index}><CitedText text={bullet} sources={edition.sources} /></li>)}</ul><span role="status">{copyMessage}</span>
              </section>
              </HeroBrief>
              <section><h2>What Moved</h2>{edition.moved.length ? edition.moved.map((paragraph, index) => <p key={index}><CitedText text={paragraph} sources={edition.sources} /></p>) : <p className="rd-muted">No market commentary in this edition.</p>}</section>
              <section id="research-derivatives"><h2>Derivatives Tone</h2>{edition.derivatives.length ? <Metrics rows={edition.derivatives} derivatives /> : <p className="rd-muted">No verified derivatives readings in this edition.</p>}</section>
              <section><h2>Flows &amp; Macro</h2>{edition.flows.length ? <Metrics rows={edition.flows} /> : <p className="rd-muted">No verified flows or macro readings in this edition.</p>}</section>
              <section><h2>Top Stories</h2><div className="rd-filters" role="group" aria-label="Filter stories by topic">{tags.map(tag => <button key={tag} type="button" aria-pressed={tag === activeTag} onClick={() => setActiveTag(tag)}>{tag}</button>)}</div>
                <div className="rd-stories">{stories.filter(story => activeTag === "All" || story.tag === activeTag).map((story, index) => <StoryRow as="div" className="rd-story" key={index}><div className="rd-story-chips"><Chip>{story.asset}</Chip><Chip>{story.tag}</Chip></div><div><p><CitedText text={story.text.replace(/\s*\(Source[^)]*\)\.?\s*$/i, "")} sources={edition.sources} /> <SourceRef index={story.sourceIndex} /></p>{story.whyItMatters && <p className="rd-story-why"><CitedText text={story.whyItMatters} sources={edition.sources} /></p>}</div></StoryRow>)}</div>
                {!edition.stories.length && <p className="rd-muted">No stories in this edition.</p>}
              </section>
              <p className="rd-bottomline"><strong>Bottom line</strong> <CitedText text={edition.bottomLine} sources={edition.sources} /></p>
              <section><h2>Sources</h2><details className="rd-source-details"><summary>View {edition.sources.length} sources and check times</summary><ol className="rd-sources">{edition.sources.map((source, index) => <li id={`rd-source-${index + 1}`} key={index}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>{source.checkedAt && <span className="rd-source-time">Checked <time dateTime={source.checkedAt}>{new Date(source.checkedAt).toLocaleString("en-US", {timeZone: "UTC", timeZoneName: "short"})}</time></span>}</li>)}</ol></details></section>
            </article>
            <aside className="rd-rail" aria-label="Calendar, levels and archive">
              <section className="rd-panel"><h2>What’s Next</h2><ul className="rd-calendar">{edition.calendar.map((event, index) => <CalendarItem key={index} event={event}/>)}</ul>
                {!edition.calendar.length && <p className="rd-muted">No verified upcoming events in this edition.</p>}</section>
              <section className="rd-panel"><h2>Key Levels</h2>{edition.levels.map(raw => normalizeLevels(raw, edition.tape)).map((level, index) => <div className="rd-level" key={index}><h3>{level.asset}</h3>
                {["resistance", "currentRange", "support"].map(side => <div className={`rd-level-group rd-${side}`} key={side}><h4>{side === 'currentRange' ? 'Current range' : side}</h4>{level[side]?.map((band, bandIndex) => <LevelBand key={bandIndex} band={band}/>)}{!level[side]?.length && side !== 'currentRange' && <MissingBadge/>}</div>)}
                {!!level.zones?.length && <div className="rd-level-group rd-liquidation"><h4>AEON liquidation zones</h4>{level.zones.map((zone, zoneIndex) => <ZoneBand key={zoneIndex} zone={zone}/>)}</div>}
                <p className="rd-validity">{level.validity}</p></div>)}{!edition.levels.length && <p className="rd-muted">No published levels in this edition.</p>}</section>
              <section className="rd-panel"><h2>Weekly Recap</h2><ul className="rd-recap">{edition.recap.map((call, index) => <li key={index}><Chip tone={call.result === "Hit" ? "good" : call.result === "Invalidated" ? "bad" : call.result === "Missed" ? "warn" : "neutral"}>{call.result}</Chip><strong>{call.call}</strong><p>{call.note}</p></li>)}</ul><p className="rd-muted">{edition.recapNote || (!edition.recap.length ? "The weekly recap will appear when graded calls are published." : "")}</p></section>
              <section className="rd-panel"><h2>Archive <small>{editions.length} editions</small></h2><Archive editions={editions} current={edition.date} /><Link to="/research/archive">View archive</Link></section>
            </aside>
          </>}
    </main>
    <footer className="rd-disclaimer">{DISCLAIMER}</footer>
  </div>;
}
